<template>
    <section class="speaker-list" aria-label="Speaker list">
        <div class="datatable-toolbar speaker-toolbar">
            <div class="speaker-search">
                <label class="sr-only" for="speaker-search">Search speakers</label>
                <input id="speaker-search" type="search" class="form-control" placeholder="Search speakers" v-model="dataFilter.searchText" @keyup.enter="doFind">
                <button class="btn btn-outline-primary" type="button" @click="doFind">Search</button>
                <button v-if="dataFilter.searchText" class="btn btn-link text-secondary clear-search" type="button" @click="clearSearch">Clear</button>
            </div>

            <RouterLink to="/speaker/add" class="btn btn-primary add-speaker-button">
                <i class="pi pi-plus mr-2" aria-hidden="true"></i>
                Add speaker
            </RouterLink>
        </div>

        <div v-if="isLoading || dataList.length" class="table-responsive table-container speaker-table-wrapper">
            <table class="table table-striped speaker-table">
                <thead>
                <tr>
                    <th scope="col">No</th>
                    <th scope="col">Name</th>
                    <th scope="col">Company</th>
                    <th scope="col">Current position</th>
                    <th scope="col">Priority</th>
                    <th scope="col">Profile photo</th>
                    <th scope="col">Actions</th>
                </tr>
                </thead>

                <tbody>
                <tr v-if="isLoading">
                    <td colspan="7" class="table-message">
                        <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        Loading speakers…
                    </td>
                </tr>
                <tr v-else v-for="(data, index) in dataList" :key="data.id">
                    <td class="order-number">{{ index + 1 }}</td>
                    <td class="text-left speaker-name">{{ data.name }}</td>
                    <td class="text-left">{{ data.companyName }}</td>
                    <td class="text-left speaker-position">{{ data.currentPosition }}</td>
                    <td class="text-center">{{ data.priority }}</td>
                    <td class="profile-photo-cell">
                        <Image v-if="data.image" :src="getImageUrl(data.image)" :alt="`Profile photo of ${data.name}`" width="50" preview />
                        <span v-else class="text-muted">—</span>
                    </td>
                    <td class="actions">
                        <button type="button" class="btn btn-outline-secondary" :aria-label="`Edit ${data.name}`" v-tooltip="'Edit speaker'" @click="onEdit(data)"><i class="pi pi-pencil" aria-hidden="true"></i></button>
                        <button type="button" class="btn btn-outline-danger" :aria-label="`Remove ${data.name}`" v-tooltip="'Remove speaker'" @click="onRemove(data)"><i class="pi pi-trash" aria-hidden="true"></i></button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div v-else class="speaker-empty-state" role="status">
            <i class="pi pi-users" aria-hidden="true"></i>
            <h2>{{ dataFilter.searchText ? 'No speakers match your search.' : 'No speakers yet.' }}</h2>
            <p>{{ dataFilter.searchText ? 'Try a different keyword or clear the search.' : 'Add a speaker to start building your event directory.' }}</p>
            <button v-if="dataFilter.searchText" class="btn btn-outline-primary" type="button" @click="clearSearch">Clear search</button>
            <RouterLink v-else to="/speaker/add" class="btn btn-primary">
                <i class="pi pi-plus mr-2" aria-hidden="true"></i>
                Add speaker
            </RouterLink>
        </div>

        <ConfirmDialog></ConfirmDialog>
    </section>
</template>

<script>
import {defineComponent} from 'vue'
import ConfirmDialog from "primevue/confirmdialog";
import apiService from "@/services/ApiService";
import Image from "primevue/image";

export default defineComponent({
    name: "SpeakerList",
    components: {
        ConfirmDialog, Image
    },
    data() {
        return {
            dataList: [],
            isLoading: true,
            dataFilter: {
                searchText: ''
            }
        }
    },
    methods: {
        onEdit(data) {
            this.$router.push(`/speaker/edit/${data.id}`);
        },
        onRemove(data) {
            this.$confirm.require({
                message: `Remove ${data.name} from the speaker directory?`,
                header: 'Remove speaker',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => {
                    this.doRemove(data.id);
                }
            });
        },
        doFind() {
            const params = {
                SearchText: this.dataFilter.searchText
            };
            this.isLoading = true;
            apiService.get('/speaker', {params: params})
                .then(response => {
                    this.dataList = response.data || [];
                }).catch(error => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get speakers',
                    detail: error.response?.data?.Message || 'Please try again.',
                    life: 5000
                });
            }).finally(() => {
                this.isLoading = false;
            });
        },
        clearSearch() {
            this.dataFilter.searchText = '';
            this.doFind();
        },
        doRemove(id) {
            apiService.delete(`/speaker/${id}`)
                .then(() => {
                    this.doFind();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Speaker has been removed',
                        life: 5000
                    });
                }).catch(error => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to remove speaker',
                    detail: error.response?.data?.Message || 'Please try again.',
                    life: 5000
                });
            });
        },
        getImageUrl(image){
            return `${apiService.defaults.baseURL}/file/${image}`;
        }
    },
    mounted() {
        this.doFind();
    }
})
</script>

<style scoped>
.speaker-toolbar {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.speaker-search {
    align-items: center;
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.speaker-search .form-control {
    max-width: 19rem;
}

.clear-search {
    font-size: 0.875rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
}

.add-speaker-button {
    white-space: nowrap;
}

.speaker-table-wrapper {
    margin: 0;
}

.speaker-table th {
    white-space: nowrap;
}

.speaker-name {
    font-weight: 600;
    min-width: 13rem;
}

.speaker-position {
    min-width: 12rem;
}

.profile-photo-cell {
    min-width: 7rem;
    text-align: center;
}

.table-message {
    color: #6c757d;
    padding: 2.5rem 1rem !important;
    text-align: center;
}

.speaker-empty-state {
    align-items: center;
    border: 1px solid #e2e6ec;
    border-radius: 0.65rem;
    color: #6c757d;
    display: flex;
    flex-direction: column;
    padding: 3rem 1.5rem;
    text-align: center;
}

.speaker-empty-state > .pi {
    color: #426cc6;
    font-size: 1.9rem;
    margin-bottom: 0.85rem;
}

.speaker-empty-state h2 {
    color: #343a40;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 0.35rem;
}

.speaker-empty-state p {
    margin: 0 0 1rem;
}

@media (max-width: 767.98px) {
    .speaker-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .speaker-search .form-control {
        max-width: none;
        width: 100%;
    }

    .add-speaker-button {
        align-self: flex-start;
    }
}
</style>
